import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import RangeSlider from 'react-range-slider-input';

import './RangeSlider.css';
import 'react-range-slider-input/dist/style.css';

export interface RangeSliderProps {
	name: string;
	className?: string;
	label?: string | React.ReactElement;
	min: number; // minimum selectable value
	max: number; // maximum selectable value
	step?: number;
	initialValue?: [number, number]; // [min, max]: initial slider min and max values
	fieldValue: [number, number];
	handleChange?: (newValue: [number, number]) => void | undefined;
}

export const RangeSliderV2 = (props: RangeSliderProps) => {
	const {
		name,
		className = '',
		min,
		max,
		step = 1,
		initialValue,
		fieldValue,
		handleChange,
	} = props;
	const currentValue = initialValue;
	const initialValueMin = currentValue && currentValue[0] >= min ? currentValue[0] : min;
	const initialValueMax = currentValue && currentValue[1] <= max ? currentValue[1] : max;
	const [value, setValue] = useState([initialValueMin, initialValueMax]);
	const [minInputValue, setMinInputValue] = useState(`${initialValueMin}`);
	const [maxInputValue, setMaxInputValue] = useState(`${initialValueMax}`);
	const [inputError, setInputError] = useState(false);
	const valueTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const maxStrLength = `${max}`.length;

	useEffect(() => {
		if (JSON.stringify(value) !== JSON.stringify(fieldValue)) {
			const newValue: [number, number] = [
				value && value[0] > min ? value[0] : min,
				value && value[1] < max ? value[1] : max,
			];

			handleChange && handleChange(newValue);
		}
	}, [value, max, min, fieldValue, handleChange]);

	const sliderChange = (newValue: number[]) => {
		// inputs and slider values are different in order to handle invalid inputs without breaking the slider
		setMinInputValue(`${newValue[0]}`);
		setMaxInputValue(`${newValue[1]}`);
		setInputError(false);
		valueTimer.current && clearTimeout(valueTimer.current);
		setValue(newValue);
	};

	const inputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const targetName = e.target.name;
		const newVal = e.target.value.replace(/\D/g, '');
		setInputError(false);
		let newMin = value[0];
		let newMax = value[1];
		if (targetName.includes('min')) {
			setMinInputValue(newVal);
			newMin = parseInt(newVal) || min;
			if (newVal.length === 0 || newMin < min || newMin > newMax) {
				setInputError(true);
			}
		} else if (targetName.includes('max')) {
			setMaxInputValue(newVal);
			// If the input is empty, default to max; otherwise, parse the input as an integer (including '0')
			newMax = newVal === '' ? max : parseInt(newVal);
			if (newVal.length === 0 || newMax < newMin) {
				setInputError(true);
			}

			// If the entered max value exceeds the allowed maximum, clamp it to max and update the input field
			if (newMax > max) {
				newMax = max;
				setMaxInputValue(`${newMax}`);
			}
		}
		// add a small delay to prevent the slider from moving if the user is the middle of typing
		valueTimer.current && clearTimeout(valueTimer.current);
		valueTimer.current = setTimeout(() => setValue([newMin, newMax]), 500);
	};

	return (
		<div className={`flex flex-row mt-2 ${className}`}>
			<div>
				<input
					id={name}
					type='text'
					name='input-min'
					value={minInputValue}
					onChange={inputChange}
					inputMode='numeric'
					maxLength={maxStrLength}
					className={clsx(
						inputError
							? 'text-error border-error placeholder:text-error focus:ring-error focus:border-error'
							: 'text-secondaryTextDark border-secondaryTextDark focus:ring-primaryBrand focus:border-primaryBrand',
						'block w-10 p-1 rounded h-8 border-1  text-body-sm font-normal  placeholder:text-base placeholder:text-disabledText text-center'
					)}
				/>
			</div>
			<div className='flex-grow px-2 py-3' aria-hidden='true'>
				<RangeSlider
					min={min}
					max={max}
					step={step}
					onInput={sliderChange}
					value={[value[0], value[1]]}
				/>
			</div>
			<div className='flex'>
				<input
					type='text'
					name='input-max'
					value={maxInputValue}
					onChange={inputChange}
					inputMode='numeric'
					maxLength={maxStrLength}
					className={clsx(
						inputError
							? 'text-error border-error placeholder:text-error focus:ring-error focus:border-error'
							: 'text-secondaryTextDark border-secondaryTextDark focus:ring-primaryBrand focus:border-primaryBrand',
						'block w-10 p-1 rounded h-8 border-1  text-body-sm font-normal  placeholder:text-base placeholder:text-disabledText text-center'
					)}
				/>
			</div>
		</div>
	);
};
